import React from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminProjectListItem } from '../../api/adminProjects';

interface ResetProjectModalProps {
  target: AdminProjectListItem;
  confirmText: string;
  resetting: boolean;
  onConfirmTextChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}

export default function ResetProjectModal({
  target,
  confirmText,
  resetting,
  onConfirmTextChange,
  onCancel,
  onConfirm,
}: ResetProjectModalProps) {
  const { t } = useTranslation('admin');
  const matched = confirmText === target.name;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('resetProject.dialogTitle')}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        className="card"
        style={{
          background: 'var(--surface)',
          padding: '1.2rem 1.5rem',
          minWidth: 380,
          maxWidth: 520,
          boxShadow: '0 6px 24px rgba(0,0,0,0.15)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem', color: 'var(--danger, #c0392b)' }}>
          {t('resetProject.dialogTitle')}
        </h2>
        <div style={{ fontSize: '0.88rem', color: 'var(--text-2)', marginBottom: '0.75rem' }}>
          {t('resetProject.project')}<strong>{target.name}</strong>
        </div>

        <div
          style={{
            fontSize: '0.82rem',
            color: 'var(--text-2)',
            background: 'var(--danger-bg, #fdecea)',
            border: '1px solid rgba(192,57,43,0.15)',
            borderRadius: 4,
            padding: '0.6rem 0.75rem',
            marginBottom: '1rem',
            lineHeight: 1.55,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '0.3rem', color: 'var(--danger, #c0392b)' }}>
            {t('resetProject.warningTitle')}
          </div>
          <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
            <li>{t('resetProject.warningItem1')}</li>
            <li>{t('resetProject.warningItem2')}</li>
            <li>{t('resetProject.warningItem3')}</li>
            <li>{t('resetProject.warningItem4')}</li>
            <li>{t('resetProject.warningItem5')}</li>
          </ul>
          <div style={{ marginTop: '0.5rem', color: 'var(--text-3)' }}>
            {t('resetProject.retained')}
          </div>
        </div>

        <label
          style={{
            display: 'block',
            fontSize: '0.85rem',
            color: 'var(--text-2)',
            marginBottom: '0.35rem',
          }}
        >
          {t('resetProject.inputLabel')}<code style={{ fontSize: '0.82rem' }}>{target.name}</code>
        </label>
        <input
          aria-label={t('resetProject.inputAriaLabel')}
          className="input"
          placeholder={target.name}
          value={confirmText}
          onChange={(e) => onConfirmTextChange(e.target.value)}
          autoFocus
          style={{ width: '100%', marginBottom: '0.4rem' }}
        />
        <div style={{ minHeight: '1.2rem', marginBottom: '0.6rem', fontSize: '0.78rem' }}>
          {confirmText && !matched && (
            <span style={{ color: 'var(--danger, #c0392b)' }}>{t('resetProject.nameMismatch')}</span>
          )}
        </div>

        <div
          style={{
            fontSize: '0.78rem',
            color: 'var(--text-3)',
            marginBottom: '1rem',
            lineHeight: 1.5,
          }}
        >
          {t('resetProject.auditNote')}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onCancel} disabled={resetting}>
            {t('cancel', { ns: 'common' })}
          </button>
          <button
            className="btn btn-primary"
            style={{
              background: matched && !resetting ? 'var(--danger, #c0392b)' : undefined,
              borderColor: matched && !resetting ? 'var(--danger, #c0392b)' : undefined,
            }}
            onClick={onConfirm}
            disabled={!matched || resetting}
          >
            {resetting ? t('resetProject.resetting') : t('resetProject.confirmReset')}
          </button>
        </div>
      </div>
    </div>
  );
}
